import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useTranslation } from "react-i18next";

const CATEGORIES = ["all", "video", "interview", "exhibition", "workshop", "press"];

const CAT_LABELS = {
  en: { all: "All", video: "Videos", interview: "Interviews", exhibition: "Exhibitions", workshop: "Workshops", press: "Press" },
  ar: { all: "الكل", video: "مقاطع فيديو", interview: "مقابلات", exhibition: "معارض", workshop: "ورش عمل", press: "الصحافة" },
};

function embedUrl(url) {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
  return url;
}

export default function MediaPress() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const lang = isAr ? "ar" : "en";

  const [items, setItems]     = useState([]);
  const [filter, setFilter]   = useState("all");
  const [loading, setLoading] = useState(true);
  const [player, setPlayer]   = useState(null);

  useEffect(() => {
    supabase.from("media_press").select("*").eq("is_visible", true)
      .order("sort_order").order("created_at", { ascending: false })
      .then(({ data }) => { setItems(data ?? []); setLoading(false); });
  }, []);

  const visible = filter === "all" ? items : items.filter(i => i.category === filter);
  const playerIdx = player !== null ? visible.findIndex(i => i.id === player) : -1;
  const currentItem = playerIdx >= 0 ? visible[playerIdx] : null;

  const openPlayer = useCallback((item) => {
    setPlayer(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const title = item => isAr && item?.title_ar   ? item.title_ar   : item?.title;
  const desc  = item => isAr && item?.description_ar ? item.description_ar : item?.description;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 60px" }}>
      <h1 className="spar-page-title" style={{ marginBottom: 6 }}>
        {t("mediaPress.title") || (isAr ? "الإعلام والصحافة" : "Artists Media & Press")}
      </h1>
      <p style={{ color: "var(--text-3)", marginBottom: 28, fontSize: "0.95rem" }}>
        {t("mediaPress.subtitle") || (isAr ? "مقاطع فيديو، مقابلات، معارض وورش عمل" : "Videos, interviews, exhibitions and workshops")}
      </p>

      {/* Video player panel */}
      {currentItem && (
        <div style={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 32, overflow: "hidden" }}>
          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            <iframe
              src={embedUrl(currentItem.url)}
              title={title(currentItem)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
            />
          </div>
          <div style={{ padding: "16px 20px", display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "#e0d8d0", marginBottom: 4 }} dir={isAr ? "rtl" : "ltr"}>{title(currentItem)}</div>
              {desc(currentItem) && <div style={{ fontSize: "0.875rem", color: "#888", lineHeight: 1.6 }} dir={isAr ? "rtl" : "ltr"}>{desc(currentItem)}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              <button onClick={() => playerIdx > 0 && setPlayer(visible[playerIdx - 1].id)} disabled={playerIdx <= 0} className="spar-btn" style={{ padding: "6px 12px", opacity: playerIdx <= 0 ? 0.3 : 1 }}>
                &#8592; {isAr ? "السابق" : "Prev"}
              </button>
              <button onClick={() => playerIdx < visible.length - 1 && setPlayer(visible[playerIdx + 1].id)} disabled={playerIdx >= visible.length - 1} className="spar-btn" style={{ padding: "6px 12px", opacity: playerIdx >= visible.length - 1 ? 0.3 : 1 }}>
                {isAr ? "التالي" : "Next"} &#8594;
              </button>
              <button onClick={() => setPlayer(null)} className="spar-btn" style={{ padding: "6px 12px" }}>&#10005;</button>
            </div>
          </div>
        </div>
      )}

      {/* Category filter */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => { setFilter(cat); setPlayer(null); }}
            className={`spar-btn${filter === cat ? " spar-btn-primary" : ""}`}
            style={{ padding: "6px 14px", fontSize: "0.85rem" }}
          >
            {CAT_LABELS[lang][cat]}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: "var(--text-3)" }}>{isAr ? "جارٍ التحميل…" : "Loading…"}</p>}

      {!loading && visible.length === 0 && (
        <p style={{ color: "var(--text-3)" }}>{isAr ? "لا توجد مقاطع في هذا القسم بعد." : "No media in this category yet."}</p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 }}>
        {visible.map(item => (
          <div
            key={item.id}
            onClick={() => openPlayer(item)}
            className="spar-card"
            style={{ cursor: "pointer", padding: 0, overflow: "hidden", outline: player === item.id ? "2px solid var(--accent,#bf360c)" : "none", transition: "transform 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}
          >
            <div style={{ position: "relative", paddingTop: "56.25%", background: "#1a1a1a" }}>
              {item.thumbnail_url
                ? <img src={item.thumbnail_url} alt={title(item)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, color: "#444" }}>🎬</div>}
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                <div style={{ width: 50, height: 50, borderRadius: "50%", background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#fff" }}>&#9654;</div>
              </div>
              <span style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.7)", color: "#bf360c", fontSize: 10, padding: "2px 7px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {CAT_LABELS[lang][item.category] || item.category}
              </span>
            </div>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--text-primary)", lineHeight: 1.4, marginBottom: 4 }} dir={isAr ? "rtl" : "ltr"}>{title(item)}</div>
              {desc(item) && <div style={{ fontSize: "0.8rem", color: "var(--text-3)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }} dir={isAr ? "rtl" : "ltr"}>{desc(item)}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
